<template>
  <div class="collaboration-map">
    <div
      :class="['switch-toggle', `switch-toggle--${displayType}`]"
      @click="toggleDisplay">
      <span class="switch-toggle__input"></span>
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="['switch-toggle__item', `switch-toggle__item--${tab.name}`]">
        <i :class="['fa-solid', `fa-${tab.icon}`]"></i>
      </div>
    </div>

    <!-- content -->
    <loading v-if="loading"></loading>
    <div
      v-else
      :class="[
        'collaboration-map__content',
        `collaboration-map__content--${displayType}`,
      ]">
      <div v-for="tab in activeContent" :key="tab.id">
        <!-- map -->
        <template v-if="tab.name === 'map'">
          <div class="collaboration-map__map-wrapper">
            <div class="collaboration-map__map">
              <div class="collaboration-map__map-image">
                <responsive-image :images="mapBackground" />
              </div>
            </div>

            <div class="collaboration-map__collaborations">
              <div
                v-for="({ company, positions }, idx) in orderedMap"
                :key="company.alias + positions.left"
                class="collaboration-map__collaboration"
                :style="{
                  '--top': `${positions.top}%`,
                  '--left': `${positions.left}%`,
                }">
                <div
                  :class="{
                    'collaboration-map__logo': true,
                    'collaboration-map__logo--inactive':
                      activeInfoBox !== idx + 1,
                  }"
                  @click="
                    activeInfoBox === idx + 1
                      ? (activeInfoBox = 0)
                      : (activeInfoBox = idx + 1)
                  ">
                  <responsive-image :images="company.logo" />
                </div>

                <!-- info box - teaser -->
                <transition name="appear" appear>
                  <div
                    v-if="activeInfoBox === idx + 1 && !isMobile"
                    class="collaboration-teaser">
                    <div class="collaboration-teaser__background">
                      <span
                        @click="activeInfoBox = 0"
                        class="collaboration-teaser__close"></span>
                      <responsive-image :images="company.crops.image" />
                    </div>
                    <div class="collaboration-teaser__content">
                      <div class="collaboration-teaser__logo">
                        <responsive-image :images="company.logo" />
                      </div>
                      <h3 class="collaboration-teaser__title">
                        {{ company.title }}
                      </h3>
                      <div class="collaboration-teaser__location">
                        {{ company.location }}
                      </div>
                      <div class="collaboration-teaser__link">
                        <a :href="company.alias" class="button button--primary">
                          {{ t.learnMore }}
                        </a>
                      </div>

                      <!-- arrows -->
                      <div
                        v-if="collaborations.length > 1"
                        class="collaboration-teaser__navigation">
                        <span
                          @click="setActiveInfoBox(false)"
                          class="collaboration-teaser__previous"></span>
                        <span
                          @click="setActiveInfoBox(true)"
                          class="collaboration-teaser__next"></span>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div v-if="isMobile" ref="teasers" class="collaboration-map__teasers">
            <template v-for="({ company, positions }, idx) in orderedMap">
              <div
                v-if="activeInfoBox === idx + 1"
                :key="company.alias + positions.top + positions.left"
                class="collaboration-teaser">
                <div class="collaboration-teaser__content">
                  <div class="collaboration-teaser__logo">
                    <responsive-image :images="company.logo" />
                  </div>
                  <h3 class="collaboration-teaser__title">
                    {{ company.title }}
                  </h3>
                  <div class="collaboration-teaser__location">
                    {{ company.location }}
                  </div>
                  <div class="collaboration-teaser__link">
                    <a :href="company.alias" class="button button--primary">
                      {{ t.learnMore }}
                    </a>
                  </div>

                  <!-- arrows -->
                  <div
                    v-if="collaborations.length > 1"
                    class="collaboration-teaser__navigation">
                    <span
                      @click="setActiveInfoBox(false)"
                      class="collaboration-teaser__previous"></span>
                    <span
                      @click="setActiveInfoBox(true)"
                      class="collaboration-teaser__next"></span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </template>

        <!-- list -->
        <template v-if="tab.name === 'list'">
          <div class="collaboration-map__list">
            <div class="collaboration-map__list-top">
              <div>{{ t.company }}</div>
              <div>{{ t.crop }}</div>
              <div>{{ t.city }}</div>
            </div>
            <a
              v-for="{ company, positions } in orderedList"
              :key="company.alias + positions.top"
              :href="company.alias"
              class="collaboration-map__list-item">
              <div class="collaboration-map__list-logo">
                <responsive-image :images="company.logo" />
              </div>
              <h3 class="collaboration-map__list-title">
                {{ company.title }}
              </h3>
              <div class="collaboration-map__list-crop">
                {{ company.crops.title }}
                <span>{{ `${t.in} ${company.location}` }}</span>
              </div>
              <div v-if="!isMobile" class="collaboration-map__list-location">
                {{ company.location }}
              </div>
              <div class="collaboration-map__list-link">
                <div class="button button--primary">
                  {{ t.learnMore }}
                </div>
              </div>
            </a>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
// components
import Loading from './elements/Loading';
import ResponsiveImage from './elements/ResponsiveImage';
// utils
import translationStrings from '../translations/translationStrings';
import { getMapDetails } from '../utils/api';
import { isMobile } from '../utils/isMobile';

export default {
  name: 'collaborations-map',
  components: {
    Loading,
    ResponsiveImage,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    lang: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      loading: true,
      displayType: 'map',
      tabs: [
        { id: 0, name: 'map', icon: 'location-dot' },
        { id: 1, name: 'list', icon: 'list' },
      ],
      activeInfoBox: 0,
      collaborations: [],
      mapBackground: [],
      isMobile: isMobile(),
      t: translationStrings,
    };
  },
  computed: {
    activeContent() {
      return this.tabs.filter((tab) => {
        return tab.name === this.displayType;
      });
    },
    orderedList() {
      // sort alphabetically
      return [...this.collaborations].sort((a, b) => {
        return a.company.title.localeCompare(b.company.title);
      });
    },
    orderedMap() {
      // sort by position on the map
      return [...this.collaborations].sort((a, b) => {
        return (
          a.positions.top - b.positions.top ||
          a.positions.left - b.positions.left
        );
      });
    },
  },
  watch: {
    activeInfoBox() {
      if (this.isMobile && this.activeInfoBox !== 0) {
        this.$nextTick(() => {
          this.$refs.teasers[0].scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        });
      }
    },
  },
  mounted() {
    this.fetchData();

    window.addEventListener('resize', () => {
      this.isMobile = isMobile();
    });
  },
  methods: {
    async fetchData() {
      try {
        const { data } = await getMapDetails(this.lang, this.id);
        const content = data.data;
        this.mapBackground = content.map;
        this.collaborations = content.collaboration;
      } catch (error) {
        if (error.response) {
          console.error(error.response);
        }
      } finally {
        this.loading = false;
      }
    },
    toggleDisplay() {
      this.displayType = this.displayType === 'map' ? 'list' : 'map';
    },
    setActiveInfoBox(next) {
      if (next) {
        this.activeInfoBox === this.collaborations.length
          ? (this.activeInfoBox = 1)
          : (this.activeInfoBox += 1);
      } else {
        this.activeInfoBox === 1
          ? (this.activeInfoBox = this.collaborations.length)
          : (this.activeInfoBox -= 1);
      }
    },
  },
};
</script>
